import React, { useState, useEffect } from "react";
import {
  Typography,
  Button,
  TextField,
  Box,
  Paper,
  FormControlLabel,
  Switch,
  Alert,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import * as api from "../services/api";

interface HeroSlide {
  id: number;
  title: string;
  description: string;
  imageUrl: string;
  order: number;
  isActive: boolean;
}

const HeroSlideForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    order: 0,
    isActive: true,
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (isEdit) {
      fetchSlide();
    }
  }, [id]);

  const fetchSlide = async () => {
    try {
      const response = await api.get(`/hero-slides/${id}`);
      const slide: HeroSlide = response.data;
      setFormData({
        title: slide.title,
        description: slide.description,
        order: slide.order,
        isActive: slide.isActive,
      });
      setImagePreview(api.getImageUrl(slide.imageUrl));
    } catch (error) {
      console.error("Error fetching hero slide:", error);
      alert("Gagal memuat data hero slide");
      navigate("/hero-slides");
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });

    // Clear error when user types
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      if (errors.image) {
        setErrors({ ...errors, image: "" });
      }
    }
  };

  const validate = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.title.trim()) {
      newErrors.title = "Judul wajib diisi";
    } else if (formData.title.length > 40) {
      newErrors.title = "Judul maksimal 40 karakter";
    }

    if (!formData.description.trim()) {
      newErrors.description = "Deskripsi wajib diisi";
    } else if (formData.description.length > 40) {
      newErrors.description = "Deskripsi maksimal 40 karakter";
    }

    if (!isEdit && !imageFile) {
      newErrors.image = "Gambar background wajib diupload";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      setLoading(true);
      const formDataToSend = new FormData();
      formDataToSend.append("title", formData.title);
      formDataToSend.append("description", formData.description);
      formDataToSend.append("order", formData.order.toString());
      formDataToSend.append("isActive", formData.isActive.toString());

      if (imageFile) {
        formDataToSend.append("image", imageFile);
      }

      if (isEdit) {
        await api.put(`/hero-slides/${id}`, formDataToSend);
      } else {
        await api.post("/hero-slides", formDataToSend);
      }

      navigate("/hero-slides");
    } catch (error: any) {
      console.error("Error saving hero slide:", error);
      if (error.response?.data?.error) {
        alert(error.response.data.error);
      } else {
        alert("Gagal menyimpan hero slide");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" mb={3}>
        {isEdit ? "Edit Hero Slide" : "Tambah Hero Slide"}
      </Typography>

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Box mb={3}>
            <TextField
              fullWidth
              label="Judul"
              name="title"
              value={formData.title}
              onChange={handleChange}
              error={Boolean(errors.title)}
              helperText={
                errors.title || `${formData.title.length}/40 karakter`
              }
              required
              inputProps={{ maxLength: 40 }}
            />
          </Box>

          <Box mb={3}>
            <TextField
              fullWidth
              label="Deskripsi"
              name="description"
              value={formData.description}
              onChange={handleChange}
              error={Boolean(errors.description)}
              helperText={
                errors.description ||
                `${formData.description.length}/40 karakter`
              }
              required
              inputProps={{ maxLength: 40 }}
            />
          </Box>

          <Box mb={3}>
            <TextField
              fullWidth
              type="number"
              label="Urutan"
              name="order"
              value={formData.order}
              onChange={handleChange}
              helperText="Semakin kecil angka, semakin awal ditampilkan"
            />
          </Box>

          <Box mb={3}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={handleChange}
                  name="isActive"
                />
              }
              label="Aktifkan Slide"
            />
          </Box>

          <Box mb={3}>
            <Typography variant="subtitle1" mb={1}>
              Gambar Background
            </Typography>
            <input
              accept="image/*"
              type="file"
              onChange={handleImageChange}
              style={{ marginBottom: 10 }}
            />
            {errors.image && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {errors.image}
              </Alert>
            )}
            {imagePreview && (
              <Box mt={2}>
                <img
                  src={imagePreview}
                  alt="Preview"
                  style={{
                    maxWidth: "100%",
                    maxHeight: 300,
                    objectFit: "contain",
                    borderRadius: 8,
                  }}
                />
              </Box>
            )}
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              mt={1}
            >
              Rekomendasi: Gambar dengan rasio 16:9, ukuran minimal 1920x1080px
            </Typography>
          </Box>

          <Box display="flex" gap={2}>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? "Menyimpan..." : "Simpan"}
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate("/hero-slides")}
              disabled={loading}
            >
              Batal
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default HeroSlideForm;
